import { useCallback, useEffect, useMemo, useState } from "react";
import { useGridSelection, rectContains, type SelectionRect } from "./useGridSelection";

interface FlatRow {
  product_external_id: string;
}

// Selection + Excel-style fill/paste plumbing for one editable numeric column
// of the Bottom Up grid (quantity, average selling price, ...). Each column
// gets its own instance, so a drag or paste in one never touches the other —
// they only happen to render in the same table cell.
export function useEditableGridColumn<Row extends FlatRow>(
  flatRows: Row[],
  months: string[],
  isPastMonth: (iso: string) => boolean,
  onPasteCells: (productExternalId: string, updates: { period_date: string; value: number }[]) => void,
  onCellChange: (productExternalId: string, periodDate: string, value: number) => void,
  parseInputValue: (raw: string) => number,
) {
  const rowIndexByExternalId = useMemo(() => {
    const m = new Map<string, number>();
    flatRows.forEach((r, i) => m.set(r.product_external_id, i));
    return m;
  }, [flatRows]);

  const handleFillRange = useCallback(
    (rect: SelectionRect, value: number) => {
      for (let r = rect.r0; r <= rect.r1 && r < flatRows.length; r++) {
        const row = flatRows[r];
        const updates: { period_date: string; value: number }[] = [];
        for (let c = rect.c0; c <= rect.c1 && c < months.length; c++) {
          // Past months are read-only actuals — never overwrite them.
          if (isPastMonth(months[c])) continue;
          updates.push({ period_date: months[c], value });
        }
        if (updates.length > 0) onPasteCells(row.product_external_id, updates);
      }
    },
    [flatRows, months, isPastMonth, onPasteCells],
  );

  const handleDeleteRange = useCallback(
    (rect: SelectionRect) => handleFillRange(rect, 0),
    [handleFillRange],
  );

  const { isSelected, onCellMouseDown: onSelectMouseDown, clear: clearSelection, multi, rect } =
    useGridSelection(handleDeleteRange);

  // Live fill preview: the anchor cell reports its value as the user types and
  // every other selected cell displays it. Display-only until commit.
  const [preview, setPreview] = useState<number | null>(null);
  useEffect(() => {
    if (!multi) setPreview(null);
  }, [multi]);

  // Commit from a cell input. When the cell sits inside an active multi-cell
  // selection and the user actually typed the value, fan it out to the whole
  // rectangle (Excel-style fill); otherwise it's a normal single-cell edit.
  const handleCellCommit = useCallback(
    (productExternalId: string, periodDate: string, value: number, fillSelection?: boolean) => {
      const r = rowIndexByExternalId.get(productExternalId);
      const c = months.indexOf(periodDate);
      if (fillSelection && multi && rect && r !== undefined && c !== -1 && rectContains(rect, r, c)) {
        handleFillRange(rect, value);
        clearSelection();
      } else {
        onCellChange(productExternalId, periodDate, value);
      }
    },
    [rowIndexByExternalId, months, multi, rect, handleFillRange, clearSelection, onCellChange],
  );

  // Clicking a new cell resets the selection before the edited cell's blur can
  // fire — so flush a pending typed value into the rectangle here, while the
  // rect still exists. The input marks itself committed so its blur is a no-op.
  const handleCellMouseDown = useCallback(
    (r: number, c: number, shiftKey: boolean) => {
      if (!shiftKey && multi && rect) {
        const el = document.activeElement;
        if (
          el instanceof HTMLInputElement &&
          el.dataset.dirty === "true" &&
          el.dataset.committed !== "true"
        ) {
          const er = parseInt(el.dataset.row ?? "", 10);
          const ec = parseInt(el.dataset.col ?? "", 10);
          // Skip when clicking inside the cell being edited (caret placement).
          if (!Number.isNaN(er) && !Number.isNaN(ec) && rectContains(rect, er, ec) && !(er === r && ec === c)) {
            handleFillRange(rect, parseInputValue(el.value));
            el.dataset.committed = "true";
          }
        }
      }
      onSelectMouseDown(r, c, shiftKey);
    },
    [multi, rect, handleFillRange, onSelectMouseDown, parseInputValue],
  );

  return { isSelected, onCellMouseDown: handleCellMouseDown, preview, setPreview, handleCellCommit };
}
